/**
 * Application bootstrap.
 *
 * Microsoft Entra sign-in, then the admin console for approved users.
 */
import { mount } from "./utils/dom";
import { renderAccessPending, renderEntraSignIn } from "./components/entraGate";
import { AppShell } from "./app/shell";
import { Router } from "./app/router";
import { getAuthMe, logout } from "./services/auth";
import { DashboardPage } from "./pages/dashboard";
import { SystemHealthPage } from "./pages/health";
import { SyncStatusPage } from "./pages/sync";
import { ConfigurationPage } from "./pages/configuration";
import { ServiceAccountsPage } from "./pages/services";
import { AuditLogPage } from "./pages/audit";
import { AccessLogsPage } from "./pages/accessLogs";
import { SyncHistoryPage } from "./pages/syncHistory";
import { SyncRunDetailPage } from "./pages/syncRunDetail";
import { AccessGroupsPage } from "./pages/groups";
import { ServiceCreatePage } from "./pages/serviceCreate";
import { ServiceDetailPage } from "./pages/serviceDetail";
import { GroupEditorPage } from "./pages/groupEditor";
import { AccessRequestsPage } from "./pages/accessRequests";
import { ApiError } from "./services/apiClient";
import type { AuthMe } from "./types/api";

const container = document.getElementById("app");

if (!container) {
  throw new Error("Missing #app root element.");
}

interface ActivePage {
  destroy(): void;
}

let shell: AppShell | null = null;
let router: Router | null = null;
let currentPage: ActivePage | null = null;
let signedInMe: AuthMe | null = null;

async function boot(): Promise<void> {
  try {
    const me = await getAuthMe();
    signedInMe = me;
    if (me.status === "approved") {
      try {
        startApp();
      } catch (err) {
        console.error("Failed to start admin console", err);
        mount(
          container!,
          renderEntraSignIn("Signed in, but the console failed to start. Refresh the page.")
        );
      }
      return;
    }
    showAccessPending(me.status === "rejected" ? "rejected" : "pending", me.email);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      mount(container!, renderEntraSignIn());
      return;
    }
    if (err instanceof ApiError && err.status === 503) {
      mount(container!, renderEntraSignIn(err.message));
      return;
    }
    mount(container!, renderEntraSignIn(err instanceof ApiError ? err.message : "Sign in failed."));
  }
}

function showAccessPending(status: "pending" | "rejected", email: string | null): void {
  mount(
    container!,
    renderAccessPending(status, email, () => {
      void signOut();
    })
  );
}

async function signOut(): Promise<void> {
  try {
    await logout();
  } catch {
    /* still clear the UI */
  }
  signedInMe = null;
  currentPage?.destroy();
  currentPage = null;
  shell = null;
  router = null;
  mount(container!, renderEntraSignIn());
}

function startApp(): void {
  shell = new AppShell(container!, {
    onNavigate: (path) => router?.navigate(path),
    signedInName: signedInMe?.displayName ?? null,
    signedInEmail: signedInMe?.email ?? null,
    onSignOut: () => {
      void signOut();
    },
  });
  router = buildRouter(shell);
  router.start();
}

function buildRouter(activeShell: AppShell): Router {
  const r = new Router();

  const showDashboard = () => {
    currentPage?.destroy();
    activeShell.setActiveRoute("/dashboard");
    const page = new DashboardPage();
    currentPage = page;
    page.mount(activeShell.outlet, (path) => r.navigate(path));
  };

  const showSystemHealth = () => {
    currentPage?.destroy();
    activeShell.setActiveRoute("/health");
    const page = new SystemHealthPage();
    currentPage = page;
    page.mount(activeShell.outlet, (path) => r.navigate(path));
  };

  const showSyncStatus = () => {
    currentPage?.destroy();
    activeShell.setActiveRoute("/sync");
    const page = new SyncStatusPage();
    currentPage = page;
    page.mount(activeShell.outlet, (path) => r.navigate(path));
  };

  const showConfiguration = () => {
    currentPage?.destroy();
    activeShell.setActiveRoute("/configuration");
    const page = new ConfigurationPage();
    currentPage = page;
    page.mount(activeShell.outlet, (path) => r.navigate(path));
  };

  const showServices = () => {
    currentPage?.destroy();
    activeShell.setActiveRoute("/services");
    const page = new ServiceAccountsPage();
    currentPage = page;
    page.mount(activeShell.outlet, (path) => r.navigate(path));
  };

  const showAuditLog = () => {
    currentPage?.destroy();
    activeShell.setActiveRoute("/audit");
    const page = new AuditLogPage();
    currentPage = page;
    page.mount(activeShell.outlet, (path) => r.navigate(path));
  };

  const showAccessLogs = () => {
    currentPage?.destroy();
    activeShell.setActiveRoute("/access-logs");
    const page = new AccessLogsPage();
    currentPage = page;
    page.mount(activeShell.outlet, (path) => r.navigate(path));
  };

  const showSyncHistory = () => {
    currentPage?.destroy();
    activeShell.setActiveRoute("/sync/history");
    const page = new SyncHistoryPage();
    currentPage = page;
    page.mount(activeShell.outlet, (path) => r.navigate(path));
  };

  const showSyncRunDetail = (params: { id?: string }) => {
    currentPage?.destroy();
    activeShell.setActiveRoute("/sync/history");
    const page = new SyncRunDetailPage(params.id ?? "");
    currentPage = page;
    page.mount(activeShell.outlet, (path) => r.navigate(path));
  };

  const showGroups = () => {
    currentPage?.destroy();
    activeShell.setActiveRoute("/groups");
    const page = new AccessGroupsPage();
    currentPage = page;
    page.mount(activeShell.outlet, (path) => r.navigate(path));
  };

  const showServiceCreate = () => {
    currentPage?.destroy();
    activeShell.setActiveRoute("/services");
    const page = new ServiceCreatePage();
    currentPage = page;
    page.mount(activeShell.outlet, (path) => r.navigate(path));
  };

  const showServiceDetail = (params: { id?: string }) => {
    currentPage?.destroy();
    activeShell.setActiveRoute("/services");
    const page = new ServiceDetailPage(params.id ?? "");
    currentPage = page;
    page.mount(activeShell.outlet, (path) => r.navigate(path));
  };

  const showGroupEditor = (params: { id?: string }) => {
    currentPage?.destroy();
    activeShell.setActiveRoute("/groups");
    const page = new GroupEditorPage(params.id ?? null);
    currentPage = page;
    page.mount(activeShell.outlet, (path) => r.navigate(path));
  };

  const showAccessRequests = () => {
    currentPage?.destroy();
    activeShell.setActiveRoute("/access-requests");
    const page = new AccessRequestsPage();
    currentPage = page;
    page.mount(activeShell.outlet);
  };

  r.register("/", showDashboard);
  r.register("/dashboard", showDashboard);

  r.register("/services", showServices);
  r.register("/services/new", showServiceCreate);
  r.register("/services/:id", showServiceDetail);

  r.register("/groups", showGroups);
  r.register("/groups/new", showGroupEditor);
  r.register("/groups/:id", showGroupEditor);

  r.register("/access-requests", showAccessRequests);

  r.register("/sync", showSyncStatus);
  r.register("/sync/history", showSyncHistory);
  r.register("/sync/history/:id", showSyncRunDetail);

  r.register("/audit", showAuditLog);
  r.register("/access-logs", showAccessLogs);

  r.register("/health", showSystemHealth);
  r.register("/configuration", showConfiguration);

  r.notFound(showDashboard);

  return r;
}

void boot();
